// src/i18n/translateClient.js
//
// Translates free text people typed (notes, job descriptions, scope...) by
// calling the `translateTexts` Cloud Function (defaultcodebase).
//
// - Requests made within ~40ms of each other go up in ONE call (a job popup
//   with 12 notes = 1 request, not 12).
// - Results are cached in memory for the session; the function also caches
//   every translation in Firestore (`textTranslations`), so the same note is
//   only ever sent to Google once per language.
// - On any failure the original text is shown. Nothing is ever written back
//   to the job, so the stored notes are never changed.

import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebaseConfig";

const BATCH_DELAY_MS = 40;
const MAX_PER_CALL = 50;
const MAX_TEXT_LENGTH = 5000;

const cache = new Map(); // key -> { translated, source }
const inflight = new Map(); // key -> Promise
let queue = []; // { key, text, target, resolve }
let timer = null;
let callable = null;

const keyFor = (text, target) => `${target}\u0000${text}`;

const getCallable = () => {
  if (!callable) {
    callable = httpsCallable(getFunctions(app, "us-central1"), "translateTexts");
  }
  return callable;
};

/* -------------------------------------------------------------------------- */
/* When is translation needed at all?                                         */
/* -------------------------------------------------------------------------- */

// Words that are clearly Spanish and rare in English job notes.
const SPANISH_WORDS = new Set([
  "que", "los", "las", "del", "para", "por", "con", "una", "uno", "esta", "este",
  "estan", "pero", "muy", "tambien", "porque", "hay", "hoy", "manana", "ayer",
  "trabajo", "puerta", "pared", "techo", "piso", "cliente", "listo", "terminado",
  "termine", "falta", "necesita", "necesitamos", "necesito", "cuando", "donde",
  "como", "fue", "son", "sus", "nos", "ya", "aqui", "alli", "todo", "todos",
  "nada", "bien", "mal", "agua", "llave", "codigo", "gracias", "favor", "el",
  "la", "y", "en", "de", "se", "lo", "le", "mas", "sin", "sobre", "despues",
  "antes", "hasta", "desde", "tiene", "tienen", "hacer", "hice", "hizo",
]);

/** Cheap guess, only used for old notes that have no `lang` tag. */
export const looksSpanish = (text = "") => {
  const s = String(text);
  if (/[ñ¿¡]/i.test(s)) return true;
  const words = s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .split(/[^a-z]+/)
    .filter(Boolean);
  if (!words.length) return false;
  let hits = 0;
  for (const w of words) if (SPANISH_WORDS.has(w)) hits += 1;
  // Accented vowels alone are a weak signal (names, "café"); combine with words.
  const accents = /[áéíóú]/i.test(s) ? 1 : 0;
  return hits + accents >= 2 && hits / words.length >= 0.12;
};

/**
 * Should `text` be translated for a viewer reading in `viewerLang`?
 * `hint` is the note's `lang` tag when it has one ("en" / "es").
 */
export const needsTranslation = (text, viewerLang, hint) => {
  const s = String(text || "").trim();
  if (!s || !/[a-zA-ZáéíóúñÁÉÍÓÚÑ]/.test(s)) return false;
  if (viewerLang === "es") return hint !== "es";
  // English viewer: only Spanish-looking or Spanish-tagged text.
  if (hint === "es") return true;
  if (hint === "en") return false;
  return looksSpanish(s);
};

/* -------------------------------------------------------------------------- */
/* Batching                                                                   */
/* -------------------------------------------------------------------------- */

const flush = async () => {
  timer = null;
  const batch = queue;
  queue = [];
  if (!batch.length) return;

  // Group by target language, then chunk.
  const byTarget = {};
  batch.forEach((item) => {
    (byTarget[item.target] = byTarget[item.target] || []).push(item);
  });

  await Promise.all(
    Object.entries(byTarget).map(async ([target, items]) => {
      for (let i = 0; i < items.length; i += MAX_PER_CALL) {
        const chunk = items.slice(i, i + MAX_PER_CALL);
        try {
          const res = await getCallable()({
            target,
            texts: chunk.map((x) => x.text),
          });
          const results = Array.isArray(res?.data?.results) ? res.data.results : [];
          chunk.forEach((item, idx) => {
            const r = results[idx] || {};
            const value = {
              translated: typeof r.translated === "string" && r.translated ? r.translated : item.text,
              source: r.source || "",
            };
            cache.set(item.key, value);
            inflight.delete(item.key);
            item.resolve(value);
          });
        } catch (err) {
          console.warn("translateTexts failed - showing original text:", err?.message || err);
          chunk.forEach((item) => {
            inflight.delete(item.key); // not cached, so it can retry later
            item.resolve({ translated: item.text, source: "", failed: true });
          });
        }
      }
    })
  );
};

/** Synchronous cache peek (lets components render instantly on re-open). */
export const peekTranslation = (text, target) => cache.get(keyFor(String(text || ""), target)) || null;

/**
 * Translate one text to `target` ("en" | "es").
 * Resolves { translated, source }. Never rejects.
 */
/* Long text (a full proposal scope copied into the job description) is over
   the 5,000-character limit of one translation, and used to stay in English
   for a Spanish viewer. Split it on line breaks into pieces under the limit,
   translate each, and put it back together with the same line breaks. */
const PIECE_LENGTH = 4500;
const splitLong = (s) => {
  const pieces = [];
  let cur = "";
  const pushLine = (line) => {
    // A single line longer than a piece: cut at sentence ends, then hard.
    if (line.length > PIECE_LENGTH) {
      const parts = line.split(/(?<=[.!?;])\s+/);
      parts.forEach((part) => {
        for (let i = 0; i < part.length; i += PIECE_LENGTH) pushLine(part.slice(i, i + PIECE_LENGTH));
      });
      return;
    }
    if (cur && cur.length + 1 + line.length > PIECE_LENGTH) {
      pieces.push(cur);
      cur = line;
    } else {
      cur = cur ? `${cur}\n${line}` : line;
    }
  };
  s.split("\n").forEach(pushLine);
  if (cur) pieces.push(cur);
  return pieces;
};

const translateLong = async (s, target) => {
  const key = keyFor(s, target);
  if (cache.has(key)) return cache.get(key);
  const pieces = splitLong(s);
  const results = await Promise.all(pieces.map((piece) => translateText(piece, target)));
  const value = {
    translated: results.map((r, i) => r.translated || pieces[i]).join("\n"),
    source: results.find((r) => r.source)?.source || "",
  };
  if (!results.some((r) => r.failed)) cache.set(key, value);
  return value;
};

export const translateText = (text, target) => {
  const s = String(text || "");
  if (!s.trim()) {
    return Promise.resolve({ translated: s, source: "" });
  }
  if (s.length > MAX_TEXT_LENGTH) {
    const key = keyFor(s, target);
    if (cache.has(key)) return Promise.resolve(cache.get(key));
    if (inflight.has(key)) return inflight.get(key);
    const p = translateLong(s, target).finally(() => inflight.delete(key));
    inflight.set(key, p);
    return p;
  }
  const key = keyFor(s, target);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);

  const p = new Promise((resolve) => {
    queue.push({ key, text: s, target, resolve });
  });
  inflight.set(key, p);
  if (!timer) timer = setTimeout(flush, BATCH_DELAY_MS);
  return p;
};
